import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  Trash2,
  Search,
} from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_STYLES = {
  new: 'bg-[#F25C05]/12 text-[#F25C05] border-[#F25C05]/30',
  in_progress: 'bg-[#0A4D8C]/12 text-[#0A4D8C] border-[#0A4D8C]/30',
  quoted: 'bg-[#0f6e44]/12 text-[#0f6e44] border-[#0f6e44]/30',
  closed: 'bg-[#8a94a2]/12 text-[#8a94a2] border-[#8a94a2]/30',
  rejected: 'bg-[#9b1c1c]/12 text-[#9b1c1c] border-[#9b1c1c]/30',
};

function waLink(phone, prefill = '') {
  const digits = String(phone || '').replace(/\D+/g, '');
  return `https://wa.me/${digits}${prefill ? `?text=${encodeURIComponent(prefill)}` : ''}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function AdminQuoteRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (opts.silent) setRefreshing(true);
    else setLoading(true);
    try {
      const rows = await adminApi.listQuoteRequests({
        status: filter === 'all' ? undefined : filter,
        limit: 500,
      });
      setItems(Array.isArray(rows) ? rows : []);
    } catch (e) {
      toast.error(`Failed to load: ${e.message || 'error'}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) =>
      (r.name || '').toLowerCase().includes(s) ||
      (r.phone || '').toLowerCase().includes(s) ||
      (r.email || '').toLowerCase().includes(s) ||
      (r.pet_name || '').toLowerCase().includes(s) ||
      (r.preferred_brand || '').toLowerCase().includes(s),
    );
  }, [items, search]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, in_progress: 0, quoted: 0, closed: 0, rejected: 0 };
    for (const r of items) {
      if (c[r.status] !== undefined) c[r.status] += 1;
    }
    return c;
  }, [items]);

  const patch = async (id, payload) => {
    setSaving(true);
    try {
      await adminApi.patchQuoteRequest(id, payload);
      toast.success('Saved');
      await load({ silent: true });
      // update detail view too
      if (detail?.id === id) setDetail((d) => ({ ...d, ...payload }));
    } catch (e) {
      toast.error(`Save failed: ${e.message || 'error'}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.deleteQuoteRequest(id);
      toast.success('Deleted');
      setDetail(null);
      setConfirmDelete(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(`Delete failed: ${e.message || 'error'}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="admin-quote-requests-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#8a94a2] font-bold">Homepage form</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-[#05223D]">Quote Requests</h1>
          <p className="mt-1 text-[#465B70] text-sm">
            Homepage “Get my quote” submissions. Reach out via WhatsApp or phone, then move each request through the pipeline.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => load({ silent: true })}
          className="rounded-xl"
          data-testid="admin-quote-refresh"
        >
          {refreshing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {/* Status filter tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        {[{ value: 'all', label: 'All' }, ...STATUS_OPTIONS].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              filter === opt.value
                ? 'bg-[#05223D] text-white border-[#05223D]'
                : 'bg-white text-[#05223D] border-[#eadfce] hover:border-[#05223D]/30'
            }`}
            data-testid={`admin-quote-filter-${opt.value}`}
          >
            {opt.label}
            <span className={`text-xs font-bold ${filter === opt.value ? 'text-white/80' : 'text-[#8a94a2]'}`}>
              {counts[opt.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-4 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a94a2]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, phone, email, pet, brand…"
          className="pl-9 rounded-xl"
          data-testid="admin-quote-search"
        />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-[#eadfce] bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#8a94a2]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#8a94a2]">
            No quote requests {filter !== 'all' && `with status “${filter}”`} yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#FBF6EE] text-[#05223D]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Received</th>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold">Pet</th>
                  <th className="text-left px-4 py-3 font-semibold">Brand</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-[#eadfce] hover:bg-[#FBF6EE]/40">
                    <td className="px-4 py-3 whitespace-nowrap text-[#465B70]">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-[#05223D]">{r.name}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 text-[#465B70]">
                        <a href={`tel:${r.phone}`} className="hover:text-[#05223D] inline-flex items-center gap-1">
                          <Phone size={12} /> {r.phone}
                        </a>
                        {r.email && (
                          <a href={`mailto:${r.email}`} className="hover:text-[#05223D] inline-flex items-center gap-1 text-xs">
                            <Mail size={11} /> {r.email}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#465B70]">
                      {r.pet_type}
                      {r.pet_name ? ` · ${r.pet_name}` : ''}
                      {r.pet_breed ? ` · ${r.pet_breed}` : ''}
                    </td>
                    <td className="px-4 py-3 text-[#465B70]">{r.preferred_brand || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${STATUS_STYLES[r.status] || ''} capitalize`}>
                        {r.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <a
                          href={waLink(r.phone, `Hi ${r.name}, this is SmartPaw — regarding your quote request.`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Message on WhatsApp"
                          className="p-2 rounded-lg text-[#0f6e44] hover:bg-[#0f6e44]/10"
                          data-testid={`admin-quote-wa-${r.id}`}
                        >
                          <MessageCircle size={16} />
                        </a>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setDetail(r)}
                          className="rounded-lg h-8"
                          data-testid={`admin-quote-view-${r.id}`}
                        >
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="admin-quote-detail">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-[#05223D]">{detail.name}</SheetTitle>
                <SheetDescription>
                  Received {fmtDate(detail.created_at)} · Status:{' '}
                  <span className="capitalize">{detail.status.replace('_', ' ')}</span>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <section>
                  <p className="text-xs uppercase tracking-wider text-[#8a94a2] font-bold mb-2">Contact</p>
                  <div className="space-y-1 text-sm text-[#05223D]">
                    <p><span className="text-[#465B70]">Phone:</span> {detail.phone}</p>
                    <p><span className="text-[#465B70]">Email:</span> {detail.email || '—'}</p>
                    <p><span className="text-[#465B70]">City/area:</span> {detail.city_or_area || '—'}</p>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <a
                      href={waLink(detail.phone, `Hi ${detail.name}, this is SmartPaw — regarding your quote request.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0f6e44] text-white text-xs font-semibold hover:bg-[#0d5e3a]"
                    >
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                    <a
                      href={`tel:${detail.phone}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#eadfce] text-[#05223D] text-xs font-semibold hover:bg-[#FBF6EE]"
                    >
                      <Phone size={14} /> Call
                    </a>
                    {detail.email && (
                      <a
                        href={`mailto:${detail.email}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#eadfce] text-[#05223D] text-xs font-semibold hover:bg-[#FBF6EE]"
                      >
                        <Mail size={14} /> Email
                      </a>
                    )}
                  </div>
                </section>

                <section>
                  <p className="text-xs uppercase tracking-wider text-[#8a94a2] font-bold mb-2">Pet</p>
                  <div className="space-y-1 text-sm text-[#05223D]">
                    <p><span className="text-[#465B70]">Type:</span> {detail.pet_type}</p>
                    <p><span className="text-[#465B70]">Name:</span> {detail.pet_name || '—'}</p>
                    <p><span className="text-[#465B70]">Breed:</span> {detail.pet_breed || '—'}</p>
                    <p><span className="text-[#465B70]">Age:</span> {detail.pet_age_years ?? '—'} yrs</p>
                    <p><span className="text-[#465B70]">Weight:</span> {detail.pet_weight_kg ?? '—'} kg</p>
                    <p><span className="text-[#465B70]">Preferred brand:</span> {detail.preferred_brand || '—'}</p>
                  </div>
                </section>

                {detail.allergies_notes && (
                  <section>
                    <p className="text-xs uppercase tracking-wider text-[#8a94a2] font-bold mb-2">Notes</p>
                    <p className="text-sm text-[#05223D] whitespace-pre-wrap">{detail.allergies_notes}</p>
                  </section>
                )}

                <section>
                  <p className="text-xs uppercase tracking-wider text-[#8a94a2] font-bold mb-2">Status</p>
                  <Select
                    value={detail.status}
                    onValueChange={(v) => patch(detail.id, { status: v, admin_notes: detail.admin_notes || '' })}
                  >
                    <SelectTrigger className="w-full" data-testid="admin-quote-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </section>

                <section>
                  <p className="text-xs uppercase tracking-wider text-[#8a94a2] font-bold mb-2">Admin notes</p>
                  <Textarea
                    rows={4}
                    value={detail.admin_notes || ''}
                    onChange={(e) => setDetail((d) => ({ ...d, admin_notes: e.target.value }))}
                    onBlur={() => patch(detail.id, { status: detail.status, admin_notes: detail.admin_notes || '' })}
                    placeholder="Internal notes (auto-saved on blur)…"
                    data-testid="admin-quote-notes"
                  />
                </section>

                <section className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmDelete(detail)}
                    className="rounded-xl text-[#9b1c1c] border-[#9b1c1c]/40 hover:bg-[#9b1c1c]/8"
                    data-testid="admin-quote-delete"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete request
                  </Button>
                </section>

                {saving && (
                  <p className="text-xs text-[#8a94a2] flex items-center gap-1">
                    <Loader2 size={12} className="animate-spin" /> Saving…
                  </p>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Confirm delete */}
      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete quote request?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {confirmDelete?.name}&apos;s quote request. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#9b1c1c] hover:bg-[#7a1616]"
              onClick={() => confirmDelete && remove(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
